import { set } from "date-fns";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import {
	ButtonStandard,
	FormField,
	type FormHandle,
	FormInputText,
	FormSingleSelect,
	ModalBase,
	PYForm,
	Text,
	toast,
} from "@performyard/py-components";
import type { ActionItemWithMeetingInfoJson } from "@performyard/service-contracts";

import DatePicker from "../../../../components/Inputs/DatePicker";
import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";
import { StaticToast } from "../../../../components/Toast";

import { useEditMeetingActionItem, useMeetingWorkspaceDetails } from "../../../../api/hooks";

type EditActionItemModalProps = {
	isOpen: boolean;
	handleClose: () => void;
	actionItem: ActionItemWithMeetingInfoJson | null;
};

type EmployeeValue = {
	id: string;
	name: string;
};

const formSchema = z.object({
	name: z
		.string()
		.min(1, { message: "This is a required field" })
		.max(280, { message: "Name cannot exceed 280 characters" }),
	status: z.enum(["complete", "incomplete"]),
	assignee: z
		.object({
			id: z.string(),
			name: z.string(),
		})
		.nullable(),
	dueDate: z.date().nullable(),
});

type FormValues = z.infer<typeof formSchema>;

const EditActionItemModal = ({ isOpen, handleClose, actionItem }: EditActionItemModalProps) => {
	const formRef = useRef<FormHandle<typeof formSchema>>(null);
	const [dueDateValue, setDueDateValue] = useState<Date | null>(null);

	const { mutate: editActionItem, isPending } = useEditMeetingActionItem();
	const { data: workspace } = useMeetingWorkspaceDetails(actionItem?.workspaceId || "");

	const assignee = actionItem?.participants.find((p) => p.role === "assignee");

	const workspaceParticipants = useMemo(() => {
		if (!workspace?.participants) return [];
		return workspace.participants.map((p) => ({
			id: p.userId,
			name: p.name || "",
		}));
	}, [workspace?.participants]);

	const handleFetchEmployees = useCallback(
		async (searchTerm: string) => {
			const lowerSearchTerm = searchTerm.toLowerCase();
			return workspaceParticipants.filter((participant) =>
				participant.name.toLowerCase().includes(lowerSearchTerm)
			);
		},
		[workspaceParticipants]
	);

	const getDefaultValues = (): FormValues => {
		if (!actionItem) {
			return {
				name: "",
				status: "incomplete",
				assignee: null,
				dueDate: null,
			};
		}

		const status: "complete" | "incomplete" =
			actionItem.mostRecentMeetingStatus === "deleted" ? "incomplete" : actionItem.mostRecentMeetingStatus;

		return {
			name: actionItem.name,
			status,
			assignee: assignee
				? {
						id: assignee.userId,
						name: assignee.name || "",
					}
				: null,
			dueDate: actionItem.dueDate,
		};
	};

	// Reset form when modal opens with new action item
	useEffect(() => {
		if (isOpen && actionItem && formRef.current) {
			const defaultValues = getDefaultValues();
			formRef.current.reset();
			setDueDateValue(defaultValues.dueDate);
		}
	}, [isOpen, actionItem?._id, actionItem?.dueDate]);

	const onSubmit = ({ value }: { value: FormValues }) => {
		if (!actionItem) return;

		const participants = [...actionItem.participants];
		const assigneeIndex = participants.findIndex((p) => p.role === "assignee");

		if (value.assignee?.id) {
			// Update or add assignee
			if (assigneeIndex !== -1) {
				participants[assigneeIndex] = { userId: value.assignee.id, role: "assignee" };
			} else {
				participants.push({ userId: value.assignee.id, role: "assignee" });
			}
		} else if (assigneeIndex !== -1) {
			// Remove assignee if none selected
			participants.splice(assigneeIndex, 1);
		}

		// Update meetings with new status for most recent meeting
		const updatedMeetings = actionItem.meetings.map((m) =>
			m.meetingId === actionItem.mostRecentMeetingId ? { meetingId: m.meetingId, status: value.status } : m
		);

		editActionItem(
			{
				actionItemId: actionItem._id,
				data: {
					name: value.name,
					participants,
					meetings: updatedMeetings,
					dueDate: dueDateValue ? dueDateValue.toISOString() : null,
				},
			},
			{
				onSuccess: () => {
					toast.success("Action Item Updated");
					onClose();
				},
				onError: () => {
					toast.error("Failed to update action item");
				},
			}
		);
	};

	const onClose = () => {
		if (formRef.current) {
			formRef.current.reset();
		}
		setDueDateValue(null);
		handleClose();
	};

	const statusOptions: Array<{ value: "incomplete" | "complete"; label: string }> = [
		{ value: "incomplete", label: "Incomplete" },
		{ value: "complete", label: "Complete" },
	];

	return (
		<ModalBase
			isOpen={isOpen && actionItem !== null}
			onClose={onClose}
			dangerouslySetClassName="min-h-[464px] min-w-[432px] overflow-visible"
		>
			<div className="flex flex-col px-4 pb-6 pt-4">
				<div className="flex items-center justify-center border-b border-neutral-2 pb-4">
					<Text weight="semi-bold" size="sub-heading">
						Edit Action Item
					</Text>
				</div>
				<PYForm
					ref={formRef}
					id="edit-action-item-form"
					schema={formSchema}
					defaultValues={getDefaultValues()}
					onSubmit={onSubmit}
					validateOn={["onSubmit"]}
				>
					<div className="space-y-4">
						<FormInputText
							id="name"
							label="Action Item Name"
							placeholder="Action Item Name"
							clearable
							data-testid="action-item-name-input"
						/>

						<FormSingleSelect<"incomplete" | "complete">
							id="status"
							label="Status"
							options={statusOptions}
							selectionVariant="select"
							placeholder="Select status"
						/>

						<FormField<EmployeeValue | null>
							id="assignee"
							label="Assignee"
							input={(field) => (
								<SingleSelectEmployee
									value={field.value ?? undefined}
									onChange={(employeeId, employeeName) => {
										if (employeeId && employeeName) {
											field.onChange({ id: employeeId, name: employeeName });
										} else {
											field.onChange(null);
										}
									}}
									fetchEmployees={handleFetchEmployees}
									placeholder="Make a selection"
									isClearable
								/>
							)}
						/>

						<FormField<Date | null>
							id="dueDate"
							label="Due Date"
							input={(field) => (
								<div className="relative z-[9999]">
									<DatePicker
										value={(field.value || "") as Date | string}
										onChange={(date) => {
											if (date) {
												const dateWith5PM = set(date, {
													hours: 17,
													minutes: 0,
													seconds: 0,
													milliseconds: 0,
												});
												setDueDateValue(dateWith5PM);
												field.onChange(dateWith5PM);
											} else {
												setDueDateValue(null);
												field.onChange(null);
											}
										}}
										clearable
									/>
								</div>
							)}
						/>

						{dueDateValue && (
							<div className="flex items-center justify-center">
								<StaticToast
									message="Due Date is set for 5:00pm in your current time zone"
									type="info"
									size="small"
								/>
							</div>
						)}
					</div>
					<div className="flex justify-center gap-2 border-t border-neutral-2 pt-6">
						<div className="w-fit">
							<ButtonStandard size="large" onClick={onClose} disabled={isPending}>
								Cancel
							</ButtonStandard>
						</div>
						<div className="w-fit">
							<ButtonStandard size="large" color="green" type="submit" disabled={isPending}>
								Save
							</ButtonStandard>
						</div>
					</div>
				</PYForm>
			</div>
		</ModalBase>
	);
};

export default EditActionItemModal;
